import type { VehiclePhysicsTelemetry } from '../../shared/vehicle-physics.js';
import type { PerformanceStatus } from '../performance.js';

const PHYSICS_TELEMETRY_SESSION_KEY = 'nightshift:physics-telemetry';

/** Compact, opt-in live instrumentation for tuning the shared vehicle model. */
export class PhysicsTelemetryOverlay {
  readonly element = document.createElement('aside');
  private enabled: boolean;

  constructor(root: HTMLElement, enabled = physicsTelemetryRequested()) {
    this.enabled = enabled;
    this.element.className = 'physics-telemetry';
    this.element.setAttribute('aria-label', 'Live vehicle physics telemetry');
    this.element.innerHTML = `
      <header>
        <span>LIVE MODEL</span>
        <b>PHYSICS</b>
        <small>F8 // HIDE</small>
      </header>
      <div>
        <span><small>SURFACE</small><b data-physics="surface">ROAD</b></span>
        <span><small>SPEED</small><b data-physics="speed">0 KM/H</b></span>
        <span><small>SLIP BODY</small><b data-physics="body-slip">0.0°</b></span>
        <span><small>SLIP F / R</small><b data-physics="axle-slip">0.0° / 0.0°</b></span>
        <span><small>LOAD F / R</small><b data-physics="load">52% / 48%</b></span>
        <span><small>G LONG / LAT</small><b data-physics="g-force">0.00 / 0.00</b></span>
        <span><small>STEER / YAW</small><b data-physics="yaw">0.0° / 0.0°S</b></span>
        <span><small>VELOCITY</small><b data-physics="velocity">0.0 / 0.0 M/S</b></span>
      </div>
      <div class="physics-handling">
        <span><small>YAW TARGET</small><b data-physics="yaw-target">0.0°/S</b></span>
        <span><small>YAW ACTUAL</small><b data-physics="yaw-actual">0.0°/S</b></span>
        <span><small>YAW AID</small><b data-physics="yaw-aid">0.0°/S²</b></span>
        <span><small>STEER GUARD</small><b data-physics="steer-authority">100%</b></span>
        <span><small>TYRE LAT</small><b data-physics="tyre-lateral">0.00 G</b></span>
        <span><small>ARCADE LAT</small><b data-physics="arcade-lateral">0.00 G</b></span>
        <span><small>TYRE LONG</small><b data-physics="tyre-longitudinal">0.00 G</b></span>
        <span><small>ROAD GRADE</small><b data-physics="road-grade">0.00 G</b></span>
      </div>
      <div class="physics-performance">
        <span><small>FPS TARGET</small><b data-physics="perf-fps">-- / --</b></span>
        <span><small>FRAME 50 / 95 / 99</small><b data-physics="perf-frame">-- / -- / --</b></span>
        <span><small>SIM / SUBMIT</small><b data-physics="perf-cpu">-- / -- MS</b></span>
        <span><small>GPU / SNAP P95</small><b data-physics="perf-gpu">-- / -- MS</b></span>
        <span><small>SCENE</small><b data-physics="perf-scene">-- CALLS / --K</b></span>
        <span><small>LONG TASKS</small><b data-physics="perf-long">0 / 0 MS</b></span>
        <span><small>QUALITY</small><b data-physics="perf-quality">100% // STEADY</b></span>
        <span><small>CONTROL</small><b data-physics="perf-control">NONE</b></span>
      </div>
      <footer>
        <small>GRIP USE</small>
        <span><i data-physics="grip-bar"></i></span>
        <b data-physics="grip">0%</b>
      </footer>`;
    root.append(this.element);
    this.syncVisibility();
  }

  get visible(): boolean {
    return this.enabled;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    rememberPhysicsTelemetry(this.enabled);
    this.syncVisibility();
    return this.enabled;
  }

  update(telemetry: VehiclePhysicsTelemetry): void {
    if (!this.enabled) return;
    const degrees = 180 / Math.PI;
    this.set('surface', `${telemetry.surface} // ${(telemetry.surfaceGrip * 100).toFixed(0)}%`);
    this.set('speed', `${Math.round(telemetry.speed * 3.6)} KM/H`);
    this.set('body-slip', `${signed(telemetry.bodySlipAngle * degrees, 1)}°`);
    this.set('axle-slip', `${signed(telemetry.frontSlipAngle * degrees, 1)}° / ${signed(telemetry.rearSlipAngle * degrees, 1)}°`);
    this.set('load', `${Math.round(telemetry.frontLoad * 100)}% / ${Math.round(telemetry.rearLoad * 100)}%`);
    this.set('g-force', `${signed(telemetry.longitudinalG, 2)} / ${signed(telemetry.lateralG, 2)}`);
    this.set('yaw', `${signed(telemetry.steerAngle * degrees, 1)}° / ${signed(telemetry.yawRate * degrees, 1)}°/S`);
    this.set('velocity', `${signed(telemetry.longitudinalSpeed, 1)} / ${signed(telemetry.lateralSpeed, 1)} M/S`);
    this.set('yaw-target', `${signed(telemetry.targetYawRate * degrees, 1)}°/S`);
    this.set('yaw-actual', `${signed(telemetry.yawRate * degrees, 1)}°/S`);
    this.set('yaw-aid', `${signed(telemetry.stabilityYawAcceleration * degrees, 1)}°/S²`);
    this.set('steer-authority', `${Math.round(telemetry.steeringAuthority * 100)}%`);
    this.set('tyre-lateral', `${signed(telemetry.tyreLateralG, 2)} G`);
    this.set('arcade-lateral', `${signed(telemetry.arcadeLateralG, 2)} G`);
    this.set('tyre-longitudinal', `${signed(telemetry.tyreLongitudinalG, 2)} G`);
    this.set('road-grade', `${signed(telemetry.slopeG, 2)} G`);
    const gripPercent = Math.round(telemetry.gripUsage * 100);
    this.set('grip', `${gripPercent}%`);
    const gripBar = this.element.querySelector<HTMLElement>('[data-physics="grip-bar"]');
    if (gripBar) gripBar.style.width = `${Math.min(100, gripPercent)}%`;
    this.element.dataset.surface = telemetry.surface.toLowerCase();
    this.element.classList.toggle('over-limit', telemetry.gripUsage > 1);
  }

  updatePerformance(status: PerformanceStatus): void {
    if (!this.enabled) return;
    this.set('perf-fps', `${status.fps} / ${status.targetFps}`);
    this.set('perf-frame', `${status.frameP50Ms.toFixed(1)} / ${status.frameP95Ms.toFixed(1)} / ${status.frameP99Ms.toFixed(1)}`);
    this.set('perf-cpu', `${timing(status.cpuMs)} / ${timing(status.renderSubmitMs)} MS`);
    this.set('perf-gpu', `${timing(status.gpuMs)} / ${timing(status.snapshotP95Ms)} MS`);
    this.set('perf-scene', `${status.drawCalls ?? '--'} CALLS / ${status.triangles === undefined ? '--' : Math.round(status.triangles / 1_000)}K`);
    this.set('perf-long', `${status.longTaskCount ?? 0} / ${Math.round(status.longTaskTotalMs ?? 0)} MS`);
    this.set('perf-quality', `${Math.round(status.renderScale * 100)}% // ${status.mode.toUpperCase()}`);
    this.set('perf-control', status.thermalFallback ? 'PACING // AUTO 30' : status.limiter.toUpperCase());
    this.element.classList.toggle('performance-alert', status.budgetExceeded === true);
  }

  destroy(): void {
    this.element.remove();
  }

  private set(field: string, value: string): void {
    const target = this.element.querySelector<HTMLElement>(`[data-physics="${field}"]`);
    if (target) target.textContent = value;
  }

  private syncVisibility(): void {
    this.element.hidden = !this.enabled;
    this.element.setAttribute('aria-hidden', String(!this.enabled));
  }
}

export function physicsTelemetryRequested(
  search = window.location.search,
  storage: Pick<Storage, 'getItem'> = window.sessionStorage,
): boolean {
  const query = new URLSearchParams(search).get('physicsDebug');
  if (query === '1') return true;
  if (query === '0') return false;
  try {
    return storage.getItem(PHYSICS_TELEMETRY_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Carries a URL opt-in through menu-to-race navigation, which intentionally
 * removes setup query parameters from the address bar. */
export function rememberPhysicsTelemetryQuery(
  search = window.location.search,
  storage: Pick<Storage, 'setItem' | 'removeItem'> = window.sessionStorage,
): void {
  const query = new URLSearchParams(search).get('physicsDebug');
  try {
    if (query === '1') storage.setItem(PHYSICS_TELEMETRY_SESSION_KEY, '1');
    else if (query === '0') storage.removeItem(PHYSICS_TELEMETRY_SESSION_KEY);
  } catch {
    // Debug state is optional; restricted storage must not block startup.
  }
}

function rememberPhysicsTelemetry(enabled: boolean): void {
  try {
    if (enabled) window.sessionStorage.setItem(PHYSICS_TELEMETRY_SESSION_KEY, '1');
    else window.sessionStorage.removeItem(PHYSICS_TELEMETRY_SESSION_KEY);
  } catch {
    // Developer instrumentation must never interfere with race startup.
  }
}

function signed(value: number, precision: number): string {
  const rounded = Math.abs(value) < 0.5 * 10 ** -precision ? 0 : value;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(precision)}`;
}

function timing(value: number | null | undefined): string {
  return value === null || value === undefined ? '--' : value.toFixed(1);
}
